
export enum DealStage {
  NEW = 'Nouveau',
  QUALIFIED = 'Qualifié',
  VISIT_SCHEDULED = 'Visite Prévue',
  PROPOSAL_SENT = 'Proposition Envoyée',
  PROPOSAL_ACCEPTED = 'Proposition Validée', // NEW
  CONTRACT_SENT = 'Contrat Envoyé', // NEW
  SIGNED = 'Signé', // GAGNÉ
  LOST = 'Perdu'
}

export enum PipelineType {
  CONCIERGE = 'Conciergerie',
  SUBLET = 'Sous-location'
}

export type LeadSource = 'Site Web' | 'Appel Entrant' | 'Airbnb' | 'Leboncoin' | 'Recommandation' | 'Réseau' | 'Autre';

export type StepType = 'EMAIL' | 'WHATSAPP' | 'CALL' | 'SMS';
export type StepStatus = 'PENDING' | 'SENT' | 'SKIPPED' | 'CANCELLED';

export interface SequenceStep {
  id: string;
  type: StepType;
  dayOffset: number; // 0 = aujourd'hui, 2 = dans 2 jours
  date: string; // ISO Date calculated
  title: string;
  content: string; // Template body
  status: StepStatus;
}

export interface SequenceTemplate {
  id: string;
  name: string;
  description: string;
  color: string; // 'red' | 'blue' | 'green' etc.
  icon: string; // Emoji character
  steps: SequenceStepTemplate[];
}

export interface SequenceStepTemplate {
  type: StepType;
  dayOffset: number;
  title: string;
  content: string;
}

export interface Agency {
  id: string;
  name: string;
  city: string;
  address: string;
  legalStructure: string;
  capital?: number; // NEW: Capital social
  siret: string;
  rcsCity?: string; // NEW: Ville du RCS
  email: string;
  phone: string;
  yousignApiKey?: string; // NEW: Clé API Yousign spécifique à l'agence
  yousignEnvironment?: 'sandbox' | 'live'; // NEW: Environnement Yousign
  placidCommissionRate?: number; // NEW: % commission Placid sur revenus nets (ex: 25 pour 25%)
}

export interface UserProfile {
  id: string;
  email: string;
  role: 'superadmin' | 'admin' | 'user' | 'prestataire_marketing';
  accessLevel: 'national' | 'local';
  firstName?: string;
  lastName?: string;
  // Prestataire marketing fields
  isSupervisor?: boolean;
  marketingProviderId?: string | null;
  marketingAgencyIds?: string[];
}

export interface UserDeletionDependencyItem {
  key: string;
  label: string;
  count: number;
  requiresReassign?: boolean;
}

export interface UserDeletionDependencies {
  blocking: UserDeletionDependencyItem[];
  informational: UserDeletionDependencyItem[];
}

export interface Owner {
  id?: string;
  agencyId?: string | null;
  civility?: 'Monsieur' | 'Madame'; // NEW
  firstName: string;
  lastName: string;
  address?: string; // NEW: Adresse postale complète
  city?: string; // Ville
  zipCode?: string; // Code postal
  email: string;
  phone: string;
  source?: LeadSource;
  newsletterOptin?: boolean; // Optin pour la newsletter Placid
  created_at?: string;
}

// --- GESTION LOCATIVE TYPES ---

export type AssetType = 'INSURANCE' | 'INTERNET' | 'ELECTRICITY' | 'WATER' | 'GAS' | 'OTHER';

export interface PropertyAsset {
    id: string;
    type: AssetType;
    provider: string; // ex: Orange, EDF
    contractNumber: string;
    startDate: string;
    endDate?: string; // Pour les alertes
    monthlyCost?: number;
    status: 'ACTIVE' | 'PENDING' | 'TERMINATED';
    documentId?: string; // Lien vers un PropertyDocument
}

// Configuration Types (Admin)
export interface DocumentCategory {
    id: string;
    name: string;
}

export interface ChecklistTemplate {
    id: string;
    contract_type: PipelineType; // 'Conciergerie' | 'Sous-location'
    label: string;
    link_url?: string; // URL partenaire
    link_label?: string; // Texte du bouton (ex: Souscrire)
    order_index: number;
}

export interface PropertyDocument {
    id: string;
    name: string; // ex: DPE, Taxe Foncière
    type: string; // pdf, img
    category?: string; // Nom de la catégorie
    uploadDate: string;
    url?: string; // Mock url / Legacy URL
    path?: string; // Storage Path (Bucket path) for secure generation
}

export interface Property {
  id?: string;
  agencyId?: string | null;
  ownerId: string;
  name?: string; // NEW: Nom commercial du bien (ex: Le Cocon Lyonnais)
  address: string;
  city: string;
  zipCode?: string;
  type: 'Appartement' | 'Maison' | 'Immeuble' | 'Autre';
  surface: number;
  bedrooms?: number;
  bathrooms?: number;
  maxGuests?: number;
  created_at?: string;
  
  // Nouveaux champs pour la gestion
  status?: 'PROSPECT' | 'MANAGED' | 'ARCHIVED'; 
  contractType?: PipelineType; // 'Conciergerie' | 'Sous-location'
  superhoteId?: string; // Legacy / autre lien API
  assets?: PropertyAsset[];
  checklist?: Record<string, boolean>; // Map of ChecklistTemplate ID -> Boolean
  documents?: PropertyDocument[];
  internalNotes?: string; // HTML Content

  // Facturation propriétaire (colonnes réelles, pas dans details jsonb)
  rrRentalId?: string | null; // Id du rental RentalReady lié
  rrRentalName?: string | null; // Cache d'affichage du nom du rental RR
  commissionRateOverride?: number | null; // % ; null = hérite du taux du contrat signé
  rebillCleaning?: boolean; // Refacturer les frais de ménage au propriétaire
  cityTaxRules?: CityTaxRules; // Règles taxe de séjour par canal ({} = défauts)
}

// Règles de taxe de séjour par canal de réservation
export type CityTaxChannel = 'AIRBNB' | 'BOOKING' | 'DIRECT';

export interface CityTaxRule {
  includeInPayout: boolean; // La taxe fait partie du versé et apparaît dans le CRG
  commissioned: boolean; // La taxe entre dans la base de commission
}

export type CityTaxRules = Partial<Record<CityTaxChannel, CityTaxRule>>;

export const DEFAULT_CITY_TAX_RULES: Record<CityTaxChannel, CityTaxRule> = {
  AIRBNB: { includeInPayout: false, commissioned: false },
  BOOKING: { includeInPayout: true, commissioned: false },
  DIRECT: { includeInPayout: false, commissioned: false },
};

// Configuration du module Facturation (system_config.billing_enabled)
export interface BillingConfig {
  enabled: boolean;
  beta: boolean; // Mode bêta : accès restreint aux agences listées
  betaAgencyIds: string[]; // Agences autorisées quand beta = true
}

export interface Note {
  id: string;
  content: string;
  createdAt: string;
  authorName: string;
}

export interface Reminder {
  id: string;
  date: string; // ISO String
  note: string;
  isDone: boolean;
  hideUntilDate?: boolean; // Si true, masque le lead dans le pipeline jusqu'à la date du reminder
  time?: string; // HH:mm si fourni => RDV (event), sinon tâche (Google Tasks)
  startAt?: string; // ISO datetime de début (présent si time renseigné)
  endAt?: string; // ISO datetime de fin
  timezone?: string; // IANA timezone (ex: Europe/Paris)
  title?: string;
  description?: string;
  location?: string;
  updatedAt?: string; // horodatage de la dernière modif locale
  deletedAt?: string | null; // soft-delete pour sync bidirectionnelle
  external?: {
    googleEventId?: string;
    googleCalendarId?: string;
    googleEtag?: string;
    googleUpdatedAt?: string;
    googleTasksId?: string;
    googleTasksListId?: string;
    kind?: 'event' | 'task';
  };
}

export interface Contract {
    id: string;
    dealId: string;
    proposalId?: string;
    type: PipelineType;
    status: 'DRAFT' | 'SENT' | 'SIGNED';
    contractNumber?: string; // NEW: Numéro unique (ex: CONT-LYO-2024-001)
    createdAt: string;
    signedAt?: string; // Timestamp of technical signature/validation in app
    
    // Variables
    startDate: string;
    duration: string;
    commissionRate?: number;
    rentAmount?: number;
    
    // Champs de rédaction
    signedCity?: string; // Fait à ...
    signedDate?: string; // Le ... (Affiché sur le PDF)

    // Yousign Integration
    yousignSignatureRequestId?: string;
    yousignStatus?: string; // 'initiated', 'pending', 'done', 'refused', 'expired'

    contentJson?: any; // For flexibility
    marketingProviderId?: string | null; // Prestataire marketing source (pour notification)
}

export interface Deal {
  id?: string;
  agencyId?: string | null;
  propertyId: string; 
  ownerId: string;
  type: PipelineType;
  stage: DealStage;
  value: number; // Revenu estimé
  monthlyRentWithCharges?: number; // Loyer mensuel charges comprises (sous-location uniquement)
  source?: LeadSource;
  needsCompletion?: boolean; // Indique si le deal nécessite une complétion de données (typiquement pour les imports Leboncoin incomplets)
  notes?: Note[];
  reminders?: Reminder[];
  sequenceSteps?: SequenceStep[];
  createdAt?: string;
  proposalId?: string; // Linked proposal
  contractId?: string; // Linked contract
  marketingProviderId?: string | null; // Prestataire marketing source
  marketingLeadId?: string | null; // Lead marketing source
}

export interface Proposal {
    id: string;
    dealId: string;
    reference?: string; // EX: PROP-2023-001
    createdAt: string;
    status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED';
    
    // Financials
    nightlyRate: number;
    occupancyRate: number; // 0.75 for 75%
    commissionRate: number; // 25 for 25%
    cleaningFee: number; // Paid by guest (neutral)
    
    // Setup Fees
    setupFee: number;
    isSetupFeeOffered: boolean;
    
    // Calculated (Snapshots)
    estimatedMonthlyRevenue: number;
    estimatedAnnualRevenue: number;
    estimatedNetOwner: number;
}

export interface ContractData {
  ownerName: string;
  ownerAddress: string;
  propertyAddress: string;
  propertySurface: string;
  rentAmount?: string;
  commissionRate?: string;
  startDate: string;
  duration?: string;
}

// --- AI USAGE TYPES ---
export type AIUsageType = 'ESTIMATE_PROPOSAL' | 'GENERATE_PROPERTY_NAME' | 'PARSE_LEBONCOIN' | 'ESTIMATE_REVENUE';

export interface AIUsage {
  id: string;
  userId: string;
  usageType: AIUsageType;
  model: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
  createdAt: string;
}

export interface AIUsageStats {
  userId: string;
  userEmail: string;
  userName: string;
  totalTokens: number;
  totalCostUsd: number;
  usageCount: number;
  usageByType: Record<AIUsageType, number>;
}

// --- ESTIMATION TYPES ---
export interface PropertyDetails {
  address: string; // Adresse complète pour l'affichage (label)
  street?: string; // Rue uniquement (name) pour l'envoi au backend
  city?: string;
  zipCode?: string;
  propertyType: 'apartment' | 'house';
  bedrooms: number;
  bathrooms: number;
  area: number;
  email?: string;
  phone?: string;
  maxGuests?: number;
}

export interface EstimationResult {
  isEligible: boolean;
  lowEstimate: number;
  highEstimate: number;
  occupancyRate: number;
  averageNightlyPrice?: number;
  description: string;
  marketAnalysis?: string;
  apiCost?: number;
  tokenCount?: {
    prompt: number;
    response: number;
    total: number;
  };
}

// --- MARKETING PROVIDERS TYPES ---

export interface MarketingProvider {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  updatedAt: string;
}

export interface MarketingProviderUser {
  id: string;
  providerId: string;
  userId: string;
  isSupervisor: boolean;
  createdAt: string;
}

// --- MARKETING LEADS TYPES ---

export type MarketingLeadStatus = 'nouveau' | 'en_qualification' | 'qualifie' | 'transfere' | 'rejete';
export type MarketingLeadSource = 'meta' | 'manual' | 'call' | 'other' | 'estimation_form' | 'leboncoin' | 'pap';

export interface MarketingLeadNote {
  id: string;
  content: string;
  createdAt: string;
  authorName?: string;
  authorId?: string | null;
}

export interface MarketingLeadDocument {
  id: string;
  leadId: string;
  name: string;
  type: string; // pdf, img, doc, etc.
  path: string; // Storage path
  size?: number; // Taille en bytes
  uploadedBy?: string | null;
  uploadedAt?: string;
  createdAt?: string;
}

export interface MarketingLead {
  id: string;
  providerId?: string | null;
  source: MarketingLeadSource;
  metaLeadId?: string | null; // NULL si manuel
  formId?: string | null; // NULL si manuel
  status: MarketingLeadStatus;
  agencyId?: string | null; // Après transfert
  transferredAt?: string | null;
  transferredBy?: string | null;
  dealId?: string | null;
  pipelineStage?: string | null; // Stage choisi lors du transfert
  rejectionReason?: string | null;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  ownerId?: string | null;
  propertyAddress?: string;
  propertyCity?: string;
  propertyZipCode?: string;
  propertyType?: string;
  propertySurface?: number;
  propertyRooms?: number;
  projectType?: string;
  campaignId?: string; // NULL si manuel
  campaignName?: string; // NULL si manuel
  rawData?: any; // JSONB
  notes?: MarketingLeadNote[]; // Notes sur le lead
  documents?: MarketingLeadDocument[]; // Fichiers joints
  receivedAt: string;
  createdAt: string;
  updatedAt: string;
  // Champs d'assignation
  assignedToUserId?: string | null; // Setter assigné
  assignedAt?: string | null; // Date d'assignation
  assignmentMethod?: 'auto' | 'manual' | 'reassigned'; // Méthode d'assignation
  assignmentReason?: string | null; // Raison pour réaffectation manuelle
  // Tâches et séquences
  reminders?: Reminder[]; // Rappels manuels
  sequenceSteps?: SequenceStep[]; // Étapes de séquence automatisées
}

export interface MarketingLeadStatusHistory {
  id: string;
  leadId: string;
  oldStatus?: string | null;
  newStatus: string;
  changedBy: string;
  comment?: string | null;
  createdAt: string;
}

export interface MarketingLeadFilters {
  status?: MarketingLeadStatus;
  source?: MarketingLeadSource;
  providerId?: string;
  campaignId?: string;
  agencyId?: string;
  search?: string; // Recherche textuelle globale
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface MarketingLeadStats {
  totalLeads: number;
  byStatus: Record<MarketingLeadStatus, number>;
  bySource: Record<MarketingLeadSource, number>;
  byCampaign: Array<{
    campaignId: string;
    campaignName: string;
    count: number;
  }>;
  bySetter: Array<{
    userId: string;
    userName: string;
    count: number;
    byStatus: Record<MarketingLeadStatus, number>;
    qualificationRate: number;
    rejectionRate: number;
  }>;
  qualificationRate: number; // (qualifiés / reçus) * 100
  rejectionRate: number; // (rejetés / reçus) * 100
  rejectionReasons: Array<{
    reason: string;
    count: number;
  }>;
}

// --- META OAUTH CONNECTION (conservé pour compatibilité) ---

export interface MetaOAuthConnection {
  id: string;
  pageId: string;
  pageName: string;
  accessToken: string; // Encrypted
  longLivedToken: string; // Encrypted
  tokenExpiresAt: string;
  refreshToken?: string; // Encrypted
  webhookSecret: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GoogleCalendarConnection {
  id: string;
  userId: string;
  agencyId?: string | null;
  googleEmail: string;
  calendarId: string;
  calendarSummary?: string | null;
  accessToken?: string | null;
  refreshToken?: string | null;
  tokenExpiresAt?: string | null;
  syncToken?: string | null;
  watchChannelId?: string | null;
  watchResourceId?: string | null;
  watchExpiresAt?: string | null;
  isActive: boolean;
  lastSyncedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

// --- EXPENSES & REVENUE TYPES ---

// Types de dépenses (selon tableau Excel)
export type ExpenseType = 
  | 'RENT'              // Loyers
  | 'CHARGES'           // Charges (Forfait de charges, Eau, Electricité/gaz, Internet, Assurance)
  | 'CLEANING'          // Ménage
  | 'MAINTENANCE'       // Aménagement entretien
  | 'INSURANCE'         // Assurance (peut être lié à PropertyAsset)
  | 'UTILITIES'         // Utilities (Eau, Electricité/gaz - peut être lié à PropertyAsset)
  | 'SOFTWARE'          // Dépenses logiciels (globales, réparties)
  | 'PLACID_COMMISSION' // Commission Placid (calculée automatiquement)
  | 'OTHER';            // Autres

export type ExpenseStatus = 'PENDING' | 'PAID' | 'CANCELLED';

export interface Expense {
  id?: string;
  agencyId?: string;
  propertyId?: string | null; // NULL si dépense globale (logiciels)
  expenseType: ExpenseType;
  category: string; // Catégorie spécifique: 'Loyers', 'Forfait de charges', 'Eau', etc.
  description?: string;
  amount: number;
  currency?: string;
  year?: number | null; // 2025 ou NULL pour dépense récurrente (toutes les années)
  month?: number | null; // 1-12 ou NULL pour dépense récurrente (tous les mois)
  dueDate?: string; // Date d'échéance
  
  // Lien avec PropertyAsset
  propertyAssetReference?: string | null; // Référence vers l'asset dans properties.details->assets
  
  // Pour dépenses logiciels
  isSoftwareExpense?: boolean;
  allocatedProperties?: string[]; // Array d'UUIDs de properties
  allocationMethod?: 'EQUAL' | 'PROPORTIONAL' | 'MANUAL';
  
  // Pour commission Placid
  isPlacidCommission?: boolean;
  commissionPercentage?: number; // % stocké pour historique
  
  supplierName?: string;
  supplierContact?: string;
  documentId?: string;
  status: ExpenseStatus;
  isRecurring?: boolean; // Si true, month et year doivent être NULL (s'applique à tous les mois)
  recurringExpenseId?: string | null; // Si renseigné, cette dépense est une surcharge mensuelle d'une dépense récurrente
  
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

export interface Revenue {
  id?: string;
  agencyId?: string;
  propertyId: string;
  year: number;
  month: number;
  
  // Ventes détaillées
  nightlyRatesTotal: number;
  taxesCollected: number;
  cleaningFeesCollected: number;
  
  // Commissions plateformes
  airbnbCommissionRate: number; // % HT
  airbnbCommissionAmount: number; // Calculé
  bookingCommissionRate: number; // %
  bookingCommissionAmount: number; // Calculé
  otherPlatformCommissions?: Record<string, { rate: number; amount: number }>;
  
  // Totaux calculés
  grossRevenue: number; // nightly + taxes + cleaning
  totalCommissions: number; // Somme des commissions
  netRevenue: number; // gross - commissions
  
  source?: 'MANUAL' | 'CHANNEL_MANAGER' | 'PMS';
  notes?: string;
  
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

export interface PropertyROI {
  propertyId: string;
  propertyName: string;
  year: number;
  month: number;
  
  // Revenus
  totalRevenue: number; // Revenus nets du mois
  totalPlacidCommission: number; // Commission Placid du mois
  
  // Dépenses
  totalExpenses: number; // Toutes dépenses du mois
  expensesByCategory: Record<string, number>;
  
  // Calculs
  netProfit: number; // totalRevenue - totalExpenses - totalPlacidCommission
  roiPercentage: number; // (netProfit / totalExpenses) * 100 si totalExpenses > 0
  margin: number; // (netProfit / totalRevenue) * 100 si totalRevenue > 0
}

export interface MonthlyFinancialSummary {
  year: number;
  month: number;
  agencyId?: string;
  
  // Totaux
  totalRevenue: number;
  totalExpenses: number;
  totalPlacidCommission: number;
  netProfit: number;
  
  // Par bien
  byProperty: Array<{
    propertyId: string;
    propertyName: string;
    revenue: number;
    expenses: number;
    placidCommission: number;
    netProfit: number;
    roi: number;
  }>;
}

export interface ExpenseFilters {
  propertyId?: string;
  agencyId?: string;
  year?: number;
  month?: number;
  expenseType?: ExpenseType;
  status?: ExpenseStatus;
  limit?: number;
  offset?: number;
}

// --- STRIPE TYPES ---

export interface StripeConfig {
  apiKeyTest?: string; // Clé API Stripe test (sk_test_...)
  apiKeyProd?: string; // Clé API Stripe production (sk_live_...)
  environment: 'test' | 'production';
  subscriptionId?: string; // ID de l'abonnement Stripe
  priceIdNational?: string; // Price ID Stripe pour utilisateur national (49€/mois)
  priceIdLocal?: string; // Price ID Stripe pour utilisateur local (25€/mois)
}

export interface StripeSubscriptionItem {
  id: string;
  priceId: string;
  quantity: number;
  type: 'national' | 'local';
}

// --- FEATUREBASE TYPES ---

declare global {
  interface Window {
    Featurebase?: (...args: any[]) => void;
  }
}

export interface FeaturebaseConfig {
  appId: string;
  organization: string;
}

export interface FeaturebaseIdentifyParams {
  organization: string;
  featurebaseJwt?: string;
  email: string;
  name: string;
  userId: string;
  profilePicture?: string;
  title?: string;
  plan?: string;
  role?: string;
  accessLevel?: string;
  locale?: string;
  companies?: Array<{
    id: string;
    name: string;
    monthlySpend?: number;
    createdAt?: string;
    [key: string]: any;
  }>;
  [key: string]: any; // Pour les custom attributes
}

export interface FeaturebaseBootParams {
  appId: string;
  featurebaseJwt?: string;
  email?: string;
  userId?: string;
  createdAt?: string;
  theme?: 'light' | 'dark';
  language?: string;
  hideDefaultLauncher?: boolean;
  [key: string]: any; // Pour les custom attributes
}

export type FeaturebaseCallback = (err?: Error | null) => void;

// ============================================================
// Facturation propriétaires (CRG) — RentalReady
// ============================================================

export interface RRConnectionStatus {
  configured: boolean; // client_id/secret enregistrés
  connected: boolean; // OAuth complété, tokens valides
  status: 'NOT_CONFIGURED' | 'CONFIGURED' | 'CONNECTED' | 'ERROR';
  lastError?: string | null;
}

export interface RRRental {
  id: string;
  name: string;
  status?: string;
  officeName?: string;
  ownerName?: string;
  cityName?: string;
  address?: string;
  shortTermCommissionRate?: string | null;
}

export interface CrgLine {
  rrReservationId: number | null; // null pour une ligne manuelle
  source: string; // Plateforme (Airbnb, Booking...)
  guestName: string;
  checkinDate: string; // YYYY-MM-DD
  checkoutDate: string; // YYYY-MM-DD
  rrStatus: string; // NEW | ARCHIVED | CANCELLED_FEE | MANUAL
  isCancellationFee: boolean;
  paidOut: number; // Versé au client = loyer + ménage + taxe séjour (hors Airbnb) − com. plateforme
  paymentFees?: number; // Frais de paiement (info/audit, non déduits)
  cityTax?: number; // Taxe de séjour incluse dans le versé (0 si Airbnb)
  cleaningFee: number; // Frais de ménage
  netRevenue: number; // CA NET = paidOut - cleaningFee
  commissionRate: number; // %
  commissionAmount: number;
  excluded: boolean; // Exclue du CRG par l'utilisateur
  manual: boolean; // Ligne ajoutée manuellement
  note?: string | null;
}

export interface CrgHeaderSnapshot {
  agencyName?: string;
  agencyAddress?: string;
  agencyCity?: string;
  agencySiret?: string;
  ownerName?: string;
  ownerAddress?: string;
  ownerCity?: string;
  ownerZipCode?: string;
  propertyName?: string;
  propertyAddress?: string;
  propertyCity?: string;
}

export type CrgStatementStatus = 'DRAFT' | 'FINALIZED';

export interface CrgStatement {
  id: string;
  agencyId: string;
  propertyId: string;
  ownerId?: string | null;
  periodYear: number;
  periodMonth: number; // 1-12
  status: CrgStatementStatus;
  rrRentalId: string;
  commissionRate: number; // Taux effectif figé à la génération
  rebillCleaning: boolean;
  lines: CrgLine[];
  headerSnapshot: CrgHeaderSnapshot;
  totalPaidOut: number;
  totalCleaning: number;
  totalNetRevenue: number;
  totalCommission: number;
  totalBilled: number;
  currency: string;
  pdfStoragePath?: string | null;
  generatedAt: string;
  finalizedAt?: string | null;
  finalizedBy?: string | null;
}

export type CrgGenerationOutcome =
  | 'created'
  | 'updated'
  | 'skipped_finalized'
  | 'skipped_unlinked'
  | 'skipped_no_rate'
  | 'error';

export interface CrgGenerationResult {
  propertyId: string;
  propertyName?: string;
  statementId?: string;
  outcome: CrgGenerationOutcome;
  message?: string;
}
